'use client'

import React from 'react'
import { outfit } from '@/fonts'

export type FloorplanDownload = {
  id: string
  label: string
  fileUrl: string
  fileName: string | null
}

export type FloorplanPlanData = {
  id: string
  planTitle: string | null
  planImageUrl: string | null
  planImageAlt: string | null
  planInfo: string | null
  planDownloads: FloorplanDownload[]
}

export function FloorplanSectionClient({
  heading,
  plans,
}: {
  heading?: string | null
  plans: FloorplanPlanData[]
}) {
  if (!plans.length) return null

  return (
    <div className="flex w-full gap-5 lg:gap-8">
      {/* Teal accent bar */}
      <div
        className="w-[30px] shrink-0 self-stretch rounded-none bg-[#4BA3B0] lg:w-5"
        aria-hidden
      />

      <div className="min-w-0 flex-1">
        {heading && (
          <h2 className="mb-8 text-[28px] font-normal leading-tight tracking-tight text-[#222]">
            {heading}
          </h2>
        )}

        <div className="flex flex-col gap-20">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className="grid w-full grid-cols-1 items-start gap-6 md:grid-cols-2 lg:gap-10"
            >
              {/* Left: title + image + info */}
              <div className="flex flex-col">
                {plan.planTitle && (
                  <h3
                    className={`${outfit.className} mb-4 text-center font-medium  text-[#1d1d1b] text-[40px] leading-[20px]`}
                  >
                    {plan.planTitle}
                  </h3>
                )}
                {plan.planImageUrl && (
                  <div className="w-full overflow-hidden" style={{ maxHeight: '430px' }}>
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={plan.planImageUrl}
                      alt={plan.planImageAlt || plan.planTitle || 'Floor plan'}
                      className="h-auto w-full object-contain"
                      style={{ maxHeight: '430px' }}
                    />
                  </div>
                )}
                {plan.planInfo && (
                  <div
                    className={`${outfit.className} mt-3 border border-[#009BAB] xl:px-4 px-0 py-2 text-center font-thin uppercase tracking-widest text-[#009BAB] text-[18px] leading-[18px]`}
                  >
                    {plan.planInfo}
                  </div>
                )}
              </div>

              {/* Right: download links */}
              <div className="flex flex-col items-center justify-center gap-3 self-stretch">
                {plan.planDownloads.map((dl) => (
                  <a
                    key={dl.id}
                    href={dl.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={dl.fileName || undefined}
                    className="text-center text-[15px] font-medium text-[#1d1d1b] no-underline transition-colors hover:text-[#1595a7]"
                  >
                    {dl.label}
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
